import { assertNever } from '@deltasierra/shared';
import { darken, lighten } from '@material-ui/core';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';

export enum ChatBubbleSize {
    full = 'full',
    large = 'large',
    medium = 'medium',
    small = 'small',
}
export enum ChatBubbleColor {
    grey = 'grey',
    primary = 'primary',
    secondary = 'secondary',
}

export enum ChatBubbleArrowPosition {
    bottomLeft = 'bottom-left',
    bottomRight = 'bottom-right',
    leftBottom = 'left-bottom',
    leftTop = 'left-top',
    rightBottom = 'right-bottom',
    rightTop = 'right-top',
    topLeft = 'top-left',
    topRight = 'top-right',
}

const MainPositionOffsetPx = '-20px';
const SecondaryPositionOffsetPx = '15px';
const MarginPositionOffsetPx = '24px';

function useColorToThemeColor(color: ChatBubbleColor): string {
    const theme = useTheme();
    switch (color) {
        case ChatBubbleColor.grey:
            return theme.palette.grey[200];
        case ChatBubbleColor.primary:
            return lighten(theme.palette.primary.light, 0.6);
        case ChatBubbleColor.secondary:
            return lighten(theme.palette.secondary.light, 0.6);
        default:
            return assertNever(color);
    }
}

const StyledChatBubble = styled.div<{
    $size: ChatBubbleSize;
    $baseColor: string;
    $arrowPosition: ChatBubbleArrowPosition;
}>`
    display: inline-block;
    position: relative;

    ${props => {
        switch (props.$size) {
            case ChatBubbleSize.small:
                return `
                width: 100px;
            `;
            case ChatBubbleSize.medium:
                return `
                width: 200px;
            `;
            case ChatBubbleSize.large:
                return `
                width: 500px;
            `;
            case ChatBubbleSize.full:
                return `
                width: calc(100% - ${MarginPositionOffsetPx});
                min-width: 100px;
            `;
            default:
                return assertNever(props.$size);
        }
    }}
    height: auto;
    min-height: 50px;

    margin: ${props => props.theme.spacing(1)}px;
    ${props => {
        switch (props.$arrowPosition) {
            case ChatBubbleArrowPosition.rightTop:
            case ChatBubbleArrowPosition.rightBottom:
                return `
                    margin-right: ${MarginPositionOffsetPx};
                `;
            case ChatBubbleArrowPosition.leftTop:
            case ChatBubbleArrowPosition.leftBottom:
                return `
                    margin-left: ${MarginPositionOffsetPx};
                `;
            case ChatBubbleArrowPosition.topLeft:
            case ChatBubbleArrowPosition.topRight:
                return `
                    margin-top: ${MarginPositionOffsetPx};
                `;
            case ChatBubbleArrowPosition.bottomLeft:
            case ChatBubbleArrowPosition.bottomRight:
                return `
                    margin-bottom: ${MarginPositionOffsetPx};
                `;
            default:
                return assertNever(props.$arrowPosition);
        }
    }}

    padding: ${props => props.theme.spacing(2)}px;

    background-color: ${props => props.$baseColor};
    /* border-color: ${props => darken(props.$baseColor, 0.2)}; */
    border-radius: 20px;
    /* border-style: solid;
    border-width: 1px; */

    word-break: break-word;

    /* box-shadow: 2px 4px 2px 0px ${props => darken(props.$baseColor, 0.2)}; */

    &:before {
        content: ' ';

        height: 0;
        width: 0;

        position: absolute;
        bottom: auto;
        left: auto;
        right: auto;
        top: auto;

        border-color: transparent;
        border-style: solid;
        border-width: 12px;

        /* filter: drop-shadow(1px 2px 0px ${props => darken(props.$baseColor, 0.2)}); */

        ${props => {
            switch (props.$arrowPosition) {
                case ChatBubbleArrowPosition.rightTop:
                    return `
                        right: ${MainPositionOffsetPx};
                        top: ${SecondaryPositionOffsetPx};

                        border-left-color: ${props.$baseColor};
                        border-top-color: ${props.$baseColor};
                    `;
                case ChatBubbleArrowPosition.rightBottom:
                    return `
                        right: ${MainPositionOffsetPx};
                        bottom: ${SecondaryPositionOffsetPx};

                        border-left-color: ${props.$baseColor};
                        border-bottom-color: ${props.$baseColor};
                    `;
                case ChatBubbleArrowPosition.leftTop:
                    return `
                        left: ${MainPositionOffsetPx};
                        top: ${SecondaryPositionOffsetPx};

                        border-right-color: ${props.$baseColor};
                        border-top-color: ${props.$baseColor};
                    `;
                case ChatBubbleArrowPosition.leftBottom:
                    return `
                        left: ${MainPositionOffsetPx};
                        bottom: ${SecondaryPositionOffsetPx};

                        border-right-color: ${props.$baseColor};
                        border-bottom-color: ${props.$baseColor};
                    `;
                case ChatBubbleArrowPosition.topLeft:
                    return `
                        left: ${SecondaryPositionOffsetPx};
                        top: ${MainPositionOffsetPx};

                        border-bottom-color: ${props.$baseColor};
                        border-left-color: ${props.$baseColor};
                    `;
                case ChatBubbleArrowPosition.topRight:
                    return `
                        right: ${SecondaryPositionOffsetPx};
                        top: ${MainPositionOffsetPx};

                        border-bottom-color: ${props.$baseColor};
                        border-right-color: ${props.$baseColor};
                    `;
                case ChatBubbleArrowPosition.bottomLeft:
                    return `
                        left: ${SecondaryPositionOffsetPx};
                        bottom: ${MainPositionOffsetPx};

                        border-top-color: ${props.$baseColor};
                        border-left-color: ${props.$baseColor};
                    `;
                case ChatBubbleArrowPosition.bottomRight:
                    return `
                        right: ${SecondaryPositionOffsetPx};
                        bottom: ${MainPositionOffsetPx};

                        border-top-color: ${props.$baseColor};
                        border-right-color: ${props.$baseColor};
                    `;

                default:
                    return assertNever(props.$arrowPosition);
            }
        }}
    }
`;

export type ChatBubbleProps = {
    arrowPosition?: ChatBubbleArrowPosition;
    children: React.ReactNode;
    color?: ChatBubbleColor;
    size?: ChatBubbleSize;
};

export function ChatBubble({
    arrowPosition = ChatBubbleArrowPosition.rightTop,
    children,
    color = ChatBubbleColor.grey,
    size = ChatBubbleSize.medium,
}: ChatBubbleProps): JSX.Element {
    const themedColor = useColorToThemeColor(color);
    return (
        <StyledChatBubble $arrowPosition={arrowPosition} $baseColor={themedColor} $size={size}>
            {children}
        </StyledChatBubble>
    );
}
ChatBubble.displayName = 'ChatBubble';
ChatBubble.size = ChatBubbleSize;
ChatBubble.color = ChatBubbleColor;
ChatBubble.arrowPosition = ChatBubbleArrowPosition;
